import { styled as muiStyled } from "@mui/material/styles";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { CardContent, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import styled from "styled-components";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Button from "src/components/core/Button";
import { UseUpload } from "src/context/UploadProductContext";
// import UploadImage from "src/components/upload/dropzone/UploadImage";
import device from "src/styles/device";

const Dialog = muiStyled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 32,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    paddingBottom: 0,
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
}));

const UploadedPreview = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  width: 120px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const FileItem = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #eee;
`;

const FileName = styled(Typography)`
  word-break: break-word;
  font-size: 0.9rem;
`;

const RemoveButton = styled(IconButton)`
  position: relative !important;
  background: rgb(255, 255, 255) !important;
  color: #e04132;
  width: 34px;
  height: 34px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #c73529;
  }
`;

const UploadBox = styled.div`
  border: 2px dashed #d0d0d4;
  border-radius: 16px;
  padding: 12px;
  text-align: center;
  background: #fbfbfb;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 150px;
  height: 150px;

  &:hover {
    border-color: #e04132;
    background: #f9f9f9;
  }

  @media ${device.tabletL} {
    padding: 40px 20px;
    min-height: 150px;
  }

  @media ${device.mobileL} {
    padding: 32px 16px;
    min-height: 180px;
  }
`;

const UploadText = styled(Typography)`
  color: #666666;
  margin-top: 8px;
  text-align: center;
  font-weight: 500;
  font-size: 0.8rem !important;
`;

const UploadHelper = styled(Typography)`
  color: #999999;
  font-size: 0.7rem !important;
  margin-top: 6px;
  text-align: center;
  line-height: 1.3;
`;

const MAX_SIZE = 4 * 1024 * 1024; // 4MB
const ACCEPT_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "image/jpg",
];

const Modal = ({ value, isOpen = false, onClose }) => {
  const [files, setFiles] = useState([]); // { file, preview }
  const { onChangeUpload } = UseUpload();
  // const initialCertificateState = { file: null };
  // const [certificate, setCertificate] = useState(initialCertificateState);

  useEffect(() => {
    return () => {
      // cleanup object URLs
      files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
    };
  }, [files]);

  // clear files and previews when modal closes
  useEffect(() => {
    if (!isOpen) {
      files.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setFiles([]);
      // setCertificate(initialCertificateState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFileSelection = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const normalized = selected
      .filter((f) => {
        if (!ACCEPT_TYPES.includes(f.type)) return false;
        if (f.size > MAX_SIZE) return false;
        return true;
      })
      .map((f) => ({
        file: f,
        preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      }));

    // avoid duplicates by name+size
    setFiles((prev) => {
      const existingKey = new Set(
        prev.map((p) => `${p.file.name}_${p.file.size}`),
      );
      const filtered = normalized.filter(
        (n) => !existingKey.has(`${n.file.name}_${n.file.size}`),
      );
      return [...prev, ...filtered];
    });

    // reset input
    e.target.value = null;
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed && removed.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = () => {
    // pass File[] into dataUpload via UseUpload
    onChangeUpload(
      null,
      "certificate_files",
      files.map((f) => f.file),
    );
    // cleanup previews and local state
    files.forEach((f) => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setFiles([]);
    onClose();
  };

  // const handleImageUpload = (file, type) => {
  //   if (file) {
  //     setCertificate((prev) => ({ ...prev, file }));
  //   }
  // };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="certificate-dialog-title"
      open={isOpen}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: 740,
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 3, textAlign: "center" }}>
        อัปโหลดเอกสาร Certificate
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            borderRadius: "50%",
            border: "1px solid var(--lightgrey-color)",
            position: "absolute",
            right: 16,
            top: 16,
            color: "var(--primary-color)",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          width: "100%",
          "& .MuiBox-root": {
            maxWidth: "530px",
            mx: "auto",
          },
        }}
      >
        <Grid container xs={8} mx="auto" mb={2} justifyContent="center">
          <Grid
            container
            justifyContent="center"
            xs={12}
            sx={{ textAlign: "center", py: 2 }}
          >
            <CardContent>
              {/* hidden file input (multiple) + visible upload box */}
              <input
                id="cer-files-input"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf"
                style={{ display: "none" }}
                onChange={handleFileSelection}
              />
              <label htmlFor="cer-files-input" style={{ cursor: "pointer" }}>
                {/* <UploadImage
                  callBackFile={(file) => handleImageUpload(file, "tis_image")}
                  sizeFile={4194304} // 4MB
                  closeAlert={false}
                > */}
                <UploadBox>
                  <PhotoOutlinedIcon
                    sx={{
                      fontSize: 45,
                      color: "#999999",
                      marginBottom: "8px",
                    }}
                  />
                  <UploadText>เลือกไฟล์ รูปภาพ หรือ PDF</UploadText>
                  <UploadHelper variant="caption">
                    รองรับ JPG, PNG, PDF ขนาดไม่เกิน 4MB ต่อไฟล์
                  </UploadHelper>
                </UploadBox>
                {/* </UploadImage> */}
              </label>
            </CardContent>
          </Grid>

          <Grid container xs={12} sx={{ mb: 2 }}>
            {files.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ px: 1 }}>
                ยังไม่ได้เลือกไฟล์
              </Typography>
            ) : (
              files.map((f, idx) => (
                <FileItem key={`${f.file.name}_${f.file.size}_${idx}`}>
                  <UploadedPreview>
                    {f.preview ? (
                      <PreviewImage src={f.preview} alt={f.file.name} />
                    ) : (
                      <PictureAsPdfIcon
                        sx={{ fontSize: 40, color: "#6C6C70" }}
                      />
                    )}
                  </UploadedPreview>

                  <div style={{ flex: 1 }}>
                    <FileName variant="body2">{f.file.name}</FileName>
                    <Typography variant="caption" color="textSecondary">
                      {(f.file.size / 1024).toFixed(0)} KB
                    </Typography>
                  </div>

                  <RemoveButton
                    size="small"
                    onClick={() => removeFile(idx)}
                    aria-label="remove"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </RemoveButton>
                </FileItem>
              ))
            )}
          </Grid>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            sx={{ width: "100%", maxWidth: 180, borderRadius: 4, mx: 2, mb: 3 }}
            disabled={files.length === 0}
          >
            อัปโหลดเอกสาร
          </Button>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
